import { resolveKeysToVarNames } from "./VariablePicker";

/** A grid/loop question has multiple underlying SPSS variables, at least one
 * tagged with a `row` (per the {Qcode}_{row}[_{col}] naming convention). */
export function isGridQuestion(dataGroup, key) {
  const varName = dataGroup?.[key]?.varName || [];
  return varName.length > 1 && varName.some((v) => v.row !== undefined && v.row !== null && v.row !== "");
}

function buildLabelLookup(items) {
  const lookup = {};
  (items || []).forEach((item) => {
    const k = item.index ?? item.name;
    if (k !== undefined && k !== null) lookup[k] = item.text || item.name || String(k);
  });
  return lookup;
}

/** Resolves a grid question's cells to {name, label} using the question's
 * row/column label metadata, falling back to the raw code when unmatched. */
export function getGridCells(dataGroup, key) {
  const q = dataGroup?.[key];
  if (!q) return [];
  const rowLookup = buildLabelLookup(q.rows);
  const colLookup = buildLabelLookup(q.columns);
  return (q.varName || []).map((v) => {
    const hasRow = v.row !== undefined && v.row !== null && v.row !== "";
    const hasCol = v.col !== undefined && v.col !== null && v.col !== "";
    const rowLabel = hasRow ? (rowLookup[v.row] || String(v.row)) : null;
    const colLabel = hasCol ? (colLookup[v.col] || String(v.col)) : null;
    const label = [rowLabel, colLabel].filter(Boolean).join(" × ") || v.Name;
    return { name: v.Name, rowLabel, colLabel, label };
  });
}

export function roleOfVarName(varNameRaw, variableTypes) {
  if (!variableTypes) return "categorical";
  return variableTypes.numeric?.includes(varNameRaw) ? "numeric" : "categorical";
}

/** Derives the {name, label, role} definitions of every NEW column a Data
 * Prep recipe produces, so they can be injected as synthetic dataGroup
 * entries and become selectable like any real question. */
export function stepsToPreparedVariables(steps, dataGroup, variableTypes) {
  const vars = [];
  (steps || []).forEach((step) => {
    if (step.type === "recode") {
      const sourceRaw = resolveKeysToVarNames(step.sourceKey ? [step.sourceKey] : [], dataGroup)[0];
      const role = step.preset === "reverse" ? "numeric" : roleOfVarName(sourceRaw, variableTypes);
      vars.push({ name: step.newName, label: step.label || `${step.newName} (recoded)`, role });
    } else if (step.type === "stack") {
      vars.push({ name: step.valueVarName, label: step.valueLabel || step.valueVarName, role: step.valueRole || "numeric" });
      vars.push({ name: step.idVarName, label: step.idLabel || step.idVarName, role: "categorical" });
    }
  });
  return vars;
}

/** Builds synthetic dataGroup-shaped entries for prepared variables so the
 * existing variable-picker/role-zone machinery (which reads dataGroup[key].varName)
 * works on them unmodified — each entry's "key" IS the raw derived column name. */
export function preparedVariablesToDataGroupEntries(preparedVars) {
  const entries = {};
  preparedVars.forEach((v) => {
    entries[v.name] = {
      Qtitle: v.label,
      type: v.role === "numeric" ? "numeric" : "singleresponse",
      varName: [{ Name: v.name }],
      _prepared: true,
    };
  });
  return entries;
}

export function stepSummaryText(step) {
  switch (step.type) {
    case "filter":
      return `Filter: ${step.logic}`;
    case "recode":
      return step.preset === "reverse"
        ? `Recode: reverse "${step.sourceKey}" → ${step.newName}`
        : `Recode: "${step.sourceKey}" → ${step.newName}`;
    case "stack":
      return `Stack: ${step.cellColumns?.length || 0} cells → ${step.idVarName} / ${step.valueVarName}`;
    default:
      return step.type;
  }
}
